import { CalendarDays } from 'lucide-react';
import { Link } from 'react-router';
import { PRIORITY_LABELS, type ProjectSummaryDto } from '@cw/shared';
import { AvatarStack } from '@/design-system/components/avatar';
import { Badge } from '@/design-system/components/badge';
import { TagChip } from '@/design-system/components/tag-picker';
import { daysUntil, formatShortDate } from '@/lib/projects';
import { cn } from '@/lib/utils';
import { PRIORITY_TONE } from './meta';

export function DueLabel({ date, done }: { date: string | null; done?: boolean }) {
  if (!date) return null;
  const d = daysUntil(date)!;
  const late = !done && d < 0;
  const soon = !done && d >= 0 && d <= 3;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-[12px]',
        late ? 'text-danger' : soon ? 'text-warning' : 'text-fg-muted',
      )}
    >
      <CalendarDays className="size-3.5" />
      {d === 0 && !done ? 'Hoje' : d === 1 && !done ? 'Amanhã' : formatShortDate(date)}
      {late && <span>atrasado</span>}
    </span>
  );
}

export function ProgressBar({ done, total, className }: { done: number; total: number; className?: string }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div
      className={cn('h-1 overflow-hidden rounded-full bg-press', className)}
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="h-full rounded-full bg-fg-muted transition-[width]" style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Card de projeto (ref. 01): prazo no topo, título, cliente; equipe e progresso embaixo. */
export function ProjectCard({
  project: p,
  dragging,
  className,
}: {
  project: ProjectSummaryDto;
  dragging?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-panel bg-raised p-3.5 shadow-[inset_0_0_0_1px_var(--color-line)] transition-[background,box-shadow,transform]',
        !dragging && 'hover:bg-overlay',
        dragging && 'rotate-[2.5deg] shadow-lift ring-1 ring-line-strong',
        className,
      )}
    >
      <span
        className="absolute inset-x-0 top-0 h-[3px]"
        style={{ background: `var(--color-label-${p.color})` }}
      />
      <div className="flex items-center justify-between gap-2">
        <DueLabel date={p.dueDate} done={p.status === 'completed'} />
        {(p.priority === 'urgent' || p.priority === 'high') && (
          <Badge tone={PRIORITY_TONE[p.priority]} className="h-5 px-2 text-[11px]">
            {PRIORITY_LABELS[p.priority]}
          </Badge>
        )}
      </div>
      <Link
        to={`/projects/p/${p.id}`}
        className="mt-2 block text-[14px] leading-snug font-medium after:absolute after:inset-0 focus-visible:outline-none"
        draggable={false}
      >
        {p.name}
      </Link>
      {p.client && (
        <p className="mt-1 flex items-center gap-1.5 text-[12.5px] text-fg-muted">
          <span
            className="size-2 rounded-full"
            style={{ background: `var(--color-label-${p.client.color})` }}
          />
          {p.client.name}
        </p>
      )}
      {p.tags.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1">
          {p.tags.slice(0, 3).map((t) => (
            <TagChip key={t.id} tag={t} />
          ))}
        </div>
      )}
      <div className="mt-3.5 flex items-center gap-3">
        {p.taskCount > 0 ? (
          <div className="flex-1">
            <ProgressBar done={p.doneCount} total={p.taskCount} />
            <p className="tabular mt-1 text-[11.5px] text-fg-subtle">
              {p.doneCount}/{p.taskCount} tarefas
            </p>
          </div>
        ) : (
          <span className="flex-1" />
        )}
        <AvatarStack people={p.members.map((m) => ({ name: m.name, src: m.avatarUrl }))} max={3} />
      </div>
    </div>
  );
}
